import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import VendorHeader from "../../component/VendorHeader";
import VendorFooter from "../../component/VendorFooter";
import { API_BASE_URL } from "../../utils/app_url";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import Sidebar from "../../component/Sidebar";
import { NotificationManager } from "react-notifications";

const PlanRequestDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { langJson } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [requestData, setRequestData] = useState(null);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  useEffect(() => {
    fetchPlanRequest();
  }, []);

  const fetchPlanRequest = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}stores/admin/plan-requests/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.authorisation.token}`,
        },
      });
      const responseData = await response.json();
      if (responseData.res === true) {
        setRequestData(responseData.data);
      } else {
        NotificationManager.error(responseData.msg || "Failed to load request details");
      }
    } catch (error) {
      console.error("Error fetching plan request:", error);
      NotificationManager.error("Error loading request details");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!window.confirm("Are you sure you want to approve this plan request?")) return;

    try {
      setApproving(true);
      const response = await fetch(`${API_BASE_URL}stores/admin/plan-requests/${id}/approve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.authorisation.token}`,
        },
      });
      const responseData = await response.json();
      if (responseData.res === true) {
        NotificationManager.success("Plan request approved successfully");
        navigate("/admin-dashboard");
      } else {
        NotificationManager.error(responseData.msg || "Failed to approve request");
      }
    } catch (error) {
      console.error("Error approving request:", error);
      NotificationManager.error("Error approving request");
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm("Are you sure you want to reject this plan request?")) return;

    try {
      setRejecting(true);
      const response = await fetch(`${API_BASE_URL}stores/admin/plan-requests/${id}/reject`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.authorisation.token}`,
        },
      });
      const responseData = await response.json();
      if (responseData.res === true) {
        NotificationManager.success("Plan request rejected successfully");
        navigate("/admin-dashboard");
      } else {
        NotificationManager.error(responseData.msg || "Failed to reject request");
      }
    } catch (error) {
      console.error("Error rejecting request:", error);
      NotificationManager.error("Error rejecting request");
    } finally {
      setRejecting(false);
    }
  };

  if (loading) {
    return (
      <div>
        <div className="dashHeader">
          <VendorHeader />
        </div>
        <div className="adminDash">
          <Sidebar />
          <div className="rightSide rightTop">
            <p style={{ textAlign: "center", padding: "40px" }}>Loading...</p>
          </div>
        </div>
        <VendorFooter />
      </div>
    );
  }

  if (!requestData) {
    return (
      <div>
        <div className="dashHeader">
          <VendorHeader />
        </div>
        <div className="adminDash">
          <Sidebar />
          <div className="rightSide rightTop">
            <p style={{ textAlign: "center", padding: "40px" }}>Request not found</p>
          </div>
        </div>
        <VendorFooter />
      </div>
    );
  }

  const { request, plan, store, vendor } = requestData;

  return (
    <div>
      <div className="dashHeader">
        <VendorHeader />
      </div>
      <div className="adminDash">
        <Sidebar />

        <div className="rightSide rightTop">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" }}>
            <h2>Plan Request Details</h2>
            <button
              onClick={() => navigate("/admin-dashboard")}
              style={{
                padding: "8px 16px",
                backgroundColor: "#999",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px",
              }}
            >
              Back
            </button>
          </div>

          <div className="plan-request-container">
            {/* Vendor Information */}
            <div className="request-section">
              <h3 style={{ marginBottom: "20px", color: "#2c3e50", fontSize: "16px", fontWeight: "600" }}>Vendor Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Name</label>
                  <p>{vendor?.name}</p>
                </div>
                <div className="info-item">
                  <label>Email</label>
                  <p>{vendor?.email}</p>
                </div>
                <div className="info-item">
                  <label>Phone</label>
                  <p>{vendor?.phone || "-"}</p>
                </div>
                <div className="info-item">
                  <label>Member Since</label>
                  <p>{vendor?.created_at}</p>
                </div>
              </div>
            </div>

            {/* Store Information */}
            <div className="request-section">
              <h3 style={{ marginBottom: "20px", color: "#2c3e50", fontSize: "16px", fontWeight: "600" }}>Store Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Store Name</label>
                  <p>{store?.name}</p>
                </div>
                <div className="info-item">
                  <label>Current Plan</label>
                  <p>{store?.active_plan_name || "-"}</p>
                </div>
                <div className="info-item">
                  <label>Store Status</label>
                  <p>{store?.store_status || "Active"}</p>
                </div>
              </div>
            </div>

            {/* Plan Information */}
            <div className="request-section">
              <h3 style={{ marginBottom: "20px", color: "#2c3e50", fontSize: "16px", fontWeight: "600" }}>Requested Plan</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Plan Name</label>
                  <p>{plan?.name}</p>
                </div>
                <div className="info-item">
                  <label>Price</label>
                  <p>EUR {plan?.price}</p>
                </div>
                <div className="info-item">
                  <label>Duration</label>
                  <p>{plan?.duration} days</p>
                </div>
              </div>
            </div>

            {/* Request Details */}
            <div className="request-section">
              <h3 style={{ marginBottom: "20px", color: "#2c3e50", fontSize: "16px", fontWeight: "600" }}>Request Details</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Amount</label>
                  <p>EUR {request?.amount}</p>
                </div>
                <div className="info-item">
                  <label>Payment Method</label>
                  <p>{request?.payment_method}</p>
                </div>
                {request?.coupon_code && (
                  <div className="info-item">
                    <label>Coupon Code</label>
                    <p>{request?.coupon_code}</p>
                  </div>
                )}
                <div className="info-item">
                  <label>Submitted At</label>
                  <p>{request?.submitted_at_full}</p>
                </div>
              </div>
            </div>

            {/* Payment Information */}
            {request?.payment_method === "Direct Debit" && (
              <div className="request-section">
                <h3 style={{ marginBottom: "20px", color: "#2c3e50", fontSize: "16px", fontWeight: "600" }}>Bank Account Details</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Account Holder</label>
                    <p>{request?.account_holder}</p>
                  </div>
                  <div className="info-item">
                    <label>IBAN</label>
                    <p>{request?.iban}</p>
                  </div>
                  <div className="info-item">
                    <label>BIC</label>
                    <p>{request?.bic}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Plan Dates */}
            <div className="request-section">
              <h3 style={{ marginBottom: "20px", color: "#2c3e50", fontSize: "16px", fontWeight: "600" }}>Plan Duration</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Payment Date</label>
                  <p>{request?.payment_date}</p>
                </div>
                <div className="info-item">
                  <label>End Date</label>
                  <p>{request?.end_date}</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="request-actions" style={{ marginTop: "40px", paddingTop: "20px", borderTop: "1px solid #e8e8e8" }}>
              <div style={{ display: "flex", gap: "15px" }}>
                <button
                  onClick={handleApprove}
                  disabled={approving}
                  style={{
                    padding: "12px 24px",
                    backgroundColor: "#4caf50",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    cursor: approving ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                    opacity: approving ? 0.7 : 1,
                  }}
                >
                  {approving ? "Approving..." : "Approve Request"}
                </button>
                <button
                  onClick={handleReject}
                  disabled={rejecting}
                  style={{
                    padding: "12px 24px",
                    backgroundColor: "#f44336",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    cursor: rejecting ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                    opacity: rejecting ? 0.7 : 1,
                  }}
                >
                  {rejecting ? "Rejecting..." : "Reject Request"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <VendorFooter />
    </div>
  );
};

export default PlanRequestDetails;
